<template>
  <b-card
    no-body
    class="quick-actions-card"
    :class="{
      'quick-actions-compact': compact && !inline,
      'quick-actions-inline': inline,
    }"
  >
    <b-card-body>
      <h5 v-if="!inline" class="quick-actions-title">Quick Actions</h5>
      <div class="quick-actions-grid">
        <b-button variant="outline-primary" class="quick-action-btn quick-action-leave" @click="$emit('apply-leave')">
          <feather-icon icon="CalendarIcon" size="16" class="mr-1" />
          <span>{{ inline ? 'Leave' : 'Apply Leave' }}</span>
        </b-button>
        <b-button variant="outline-warning" class="quick-action-btn quick-action-wfh" @click="$emit('apply-wfh')">
          <feather-icon icon="HomeIcon" size="16" class="mr-1" />
          <span>{{ inline ? 'WFH' : 'Apply WFH' }}</span>
        </b-button>
        <b-button variant="outline-info" class="quick-action-btn quick-action-req-co" @click="$emit('request-comp-off')">
          <feather-icon icon="PlusCircleIcon" size="16" class="mr-1" />
          <span>{{ inline ? 'Req. CO' : 'Request Comp Off' }}</span>
        </b-button>
        <b-button variant="outline-success" class="quick-action-btn quick-action-apply-co" @click="$emit('apply-comp-off')">
          <feather-icon icon="AwardIcon" size="16" class="mr-1" />
          <span>{{ inline ? 'Apply CO' : 'Apply Comp Off' }}</span>
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BButton } from 'bootstrap-vue'

export default {
  name: 'QuickActionsCard',
  components: {
    BCard,
    BCardBody,
    BButton,
  },
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
$qa-leave-bg: #f0fafb;
$qa-leave-text: #007a8a;
$qa-leave-hover-bg: rgba(0, 153, 172, 0.12);

$qa-wfh-bg: #fff8f0;
$qa-wfh-text: #b36b00;
$qa-wfh-hover-bg: rgba(255, 159, 67, 0.12);

$qa-req-co-bg: #f0f5ff;
$qa-req-co-text: #0056b3;
$qa-req-co-hover-bg: rgba(0, 123, 255, 0.1);

$qa-apply-co-bg: #f0faf4;
$qa-apply-co-text: #1e7e34;
$qa-apply-co-hover-bg: rgba(40, 167, 69, 0.1);

@mixin quick-action-tint($bg, $text, $hover-bg) {
  background: $bg !important;
  border: none !important;
  color: $text !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &:hover,
  &:focus,
  &:active {
    background: $hover-bg !important;
    border: none !important;
    color: $text !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
  }
}

.quick-actions-card {
  background: #ffffff;
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
}

.quick-actions-title {
  margin-bottom: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
}

.quick-actions-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.quick-action-btn {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
  border-radius: 8px;
  font-weight: 600;
  border: none !important;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.quick-action-leave {
  @include quick-action-tint($qa-leave-bg, $qa-leave-text, $qa-leave-hover-bg);
}

.quick-action-wfh {
  @include quick-action-tint($qa-wfh-bg, $qa-wfh-text, $qa-wfh-hover-bg);
}

.quick-action-req-co {
  @include quick-action-tint($qa-req-co-bg, $qa-req-co-text, $qa-req-co-hover-bg);
}

.quick-action-apply-co {
  @include quick-action-tint($qa-apply-co-bg, $qa-apply-co-text, $qa-apply-co-hover-bg);
}

.quick-actions-compact .quick-actions-title {
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.quick-actions-compact :deep(.card-body) {
  padding: 0.875rem 1rem;
}

.quick-actions-compact .quick-actions-grid {
  grid-template-columns: 1fr 1fr;
  gap: 0.4rem;
}

.quick-actions-compact .quick-action-btn {
  font-size: 0.8rem;
  padding: 0.4rem 0.5rem;
  justify-content: center;
  text-align: center;
}

/* Employee inline strip: fills shared top-row height with centered actions */
.quick-actions-inline {
  height: 100%;
  min-height: 72px;
  border: 1px solid #e8ecf0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  background: #ffffff;
}

.quick-actions-inline :deep(.card-body) {
  padding: 0.65rem 0.75rem !important;
  display: flex;
  align-items: center;
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
}

.quick-actions-inline .quick-actions-grid {
  grid-template-columns: repeat(4, 1fr);
  gap: 0.4rem;
  width: 100%;
}

.quick-actions-inline .quick-action-btn {
  font-size: 0.72rem;
  padding: 0.55rem 0.35rem;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  line-height: 1.2;
  height: auto;
  min-height: 2.5rem;
}

@media (max-width: 991px) {
  .quick-actions-inline {
    height: auto;
    min-height: 0;
    background: #ffffff;
  }

  .quick-actions-inline :deep(.card-body) {
    height: auto;
    padding: 0.55rem 0.6rem !important;
  }

  .quick-actions-inline .quick-actions-grid {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .quick-actions-inline .quick-action-btn {
    min-height: 42px;
    font-size: 0.78rem;
    padding: 0.5rem 0.45rem;
    white-space: normal;
  }
}

@media (max-width: 575px) {
  .quick-actions-compact .quick-actions-grid {
    grid-template-columns: 1fr 1fr;
  }

  .quick-actions-compact .quick-action-btn {
    justify-content: center;
    text-align: center;
    min-height: 42px;
  }

  .quick-actions-inline .quick-actions-grid {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .quick-actions-inline .quick-action-btn {
    min-height: 42px;
    font-size: 0.74rem;
  }
}
</style>
